import React, { useEffect, useState } from 'react';

import { useTranslation } from 'i18n/hooks';
import { Button, cn } from 'revibe-ui';
import { Spinner } from 'revibe-ui';

import { Pagination } from '@nextui-org/react';

import { useAuth } from 'modules/auth/hooks/useAuth';
import { NoData } from 'modules/common/components/NoData';
import { useBoolean, useToast } from 'modules/common/hooks';
import { ItemPreview } from 'modules/item/components/ItemPreview';
import { ITEM_FILTERS } from 'modules/item/utils/itemFilter';
import { useDataLayer } from 'modules/tracking/hooks/useDataLayer';

import { ItemsIcon } from 'ui/icons/ItemsIcon';
import { MarketplaceAPI } from 'revibe-api';

const PAGE_SIZE = 48;

type Props = {
  filters?: MarketplaceAPI.ItemFilters;
  resetToken?: string;
  pageSize?: number;
  onItemsLoaded?: (items: MarketplaceAPI.Item[], itemsCount: number) => void;
  onPageChanged?: (filter: MarketplaceAPI.ItemFilters) => void;
  mode?: 'scroll' | 'pages';
  list?: string;
};

export const ItemsGrid = ({
  filters = {},
  resetToken = '',
  pageSize = PAGE_SIZE,
  onItemsLoaded = () => undefined,
  onPageChanged = () => undefined,
  mode = 'scroll',
  list = 'generic',
}: Props) => {
  const { tu, language } = useTranslation('');

  const { errorToast } = useToast();
  const [isFetching, startFetching, stopFetching] = useBoolean();
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const { userID } = useAuth();
  const [items, setItems] = useState<MarketplaceAPI.Item[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const dataLayer = useDataLayer();
  const userFilters = {
    ...filters,
    [ITEM_FILTERS.USER]: userID,
  };
  const fetchPage = async (isReset = false) => {
    if (isReset) {
      setItems([]);
    }
    startFetching();
    let pageToFetch = (filters[ITEM_FILTERS.PAGE] as number) || 1;
    if (isReset) {
      pageToFetch = 1;
    }
    if (mode === 'scroll') {
      pageToFetch = page + 1;
    }

    const { data, error } = await MarketplaceAPI.getItems(
      userFilters,
      language,
      pageToFetch,
      pageSize
    );
    if (data) {
      setPage(data.currentPage);
      setHasMore(
        Boolean(
          data.res.length > 0 &&
            data.res.length === pageSize &&
            data.currentPage < data.totalPages
        )
      );
      setTotalPages(data.totalPages);
      const newItems =
        isReset || mode === 'pages' ? data.res : [...items, ...data.res];
      setItems(newItems);
      dataLayer.trackViewItemList(list, list, newItems);
      onItemsLoaded(
        newItems,
        data.res.length > pageSize ? data.totalItems : data.res.length
      );
    } else {
      errorToast(error);
    }
    stopFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    fetchPage(mode === 'scroll');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  const missingItemsGridSm = 2 - (items.length % 2);
  const missingItemsGridXl = 3 - (items.length % 3);

  const renderItemsDefault = (items: MarketplaceAPI.Item[]) =>
    items.length > 0 ? (
      <div className="box-border grid grid-cols-2 gap-[1px] bg-black xl:grid-cols-3">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={cn(
              i >= items.length - missingItemsGridSm && 'border-b border-black'
            )}
          >
            <ItemPreview item={item} list={list} />
          </div>
        ))}
        <div
          className={cn(
            'hidden h-full w-full bg-white',
            missingItemsGridSm === 1 && 'block xl:hidden'
          )}
        />
        <div
          className={cn(
            'hidden h-full w-full bg-white',
            missingItemsGridXl === 1 && 'xl:block',
            missingItemsGridXl === 2 && 'col-span-2 xl:block'
          )}
        />
      </div>
    ) : (
      <div className="flex h-96 w-full flex-col items-center">
        <NoData
          icon={<ItemsIcon className="h-12 w-12" />}
          heading={tu('no-items', 'shop')}
          text=""
        />
      </div>
    );

  return (
    <div className="w-full">
      {isFetching && !items.length ? (
        <div className="flex h-96 w-full flex-col items-center">
          <Spinner />
        </div>
      ) : (
        renderItemsDefault(items)
      )}
      {mode === 'pages' && totalPages > 0 && (
        <div className="[&_button]:font-formula mt-8 flex flex-col items-center [&_div]:rounded-none [&_div]:bg-black [&_button]:rounded-none [&_button]:border [&_button]:border-black [&_button]:bg-white [&_span]:-mb-0.5">
          <Pagination
            key={page}
            total={totalPages}
            initialPage={page}
            onChange={(p) =>
              onPageChanged({ ...filters, [ITEM_FILTERS.PAGE]: p })
            }
          />
        </div>
      )}
      {mode === 'scroll' && hasMore ? (
        <div className="mx-auto w-fit px-4 py-8">
          <Button
            isLoading={isFetching}
            disabled={isFetching}
            onClick={() => fetchPage(false)}
          >
            {tu('common.see-more')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
